<template>
  <div :style="themePageStyle">
    <div :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">
      <nav class="ff-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <a href="javascript:void(0)" @click="$router.back()" class="ff-back"><i class="mdi mdi-arrow-left me-1"></i>Back</a>
          <h6 class="m-0 fw-bold" style="color:var(--fm-h,#F5F0E8);">Feed Formulations</h6>
          <button class="btn btn-sm btn-warning" @click="openNew"><i class="mdi mdi-plus me-1"></i>New</button>
        </div>
      </nav>

      <div class="container py-3">
        <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
        <div v-else>
          <div class="ff-intro mb-3">
            <i class="mdi mdi-lightbulb-on-outline me-1" style="color:#BFA164;"></i>
            A formulation is a feed recipe — the ingredients and daily amounts you feed a group of animals, with the cost per day worked out for you. Create one for each group (e.g. calves, bulls over 300kg), then pick it when you <strong>Log Feeding</strong> and the ingredients are deducted from your feed stock automatically.
          </div>

          <h6 v-if="templateList.length" class="ff-section">Platform templates</h6>
          <div v-for="f in templateList" :key="'tpl-'+f.id" class="ff-card">
            <div class="ff-card-head">
              <div>
                <strong>{{ f.name }}</strong>
                <div class="ff-meta">
                  <span v-if="f.weight_band_min_kg || f.weight_band_max_kg"><i class="mdi mdi-scale me-1"></i>{{ f.weight_band_min_kg || '?' }}-{{ f.weight_band_max_kg || '?' }}kg</span>
                  <span><i class="mdi mdi-target me-1"></i>{{ f.target_kg_per_day }} kg/day</span>
                  <span><i class="mdi mdi-cash me-1"></i>{{ Number(f.daily_cost_kshs).toLocaleString() }} Ksh/day</span>
                </div>
              </div>
              <div class="d-flex gap-1">
                <button class="btn btn-sm btn-outline-warning" @click="openView(f.id)"><i class="mdi mdi-eye"></i></button>
                <button class="btn btn-sm btn-warning" @click="cloneTemplate(f)" :disabled="cloning===f.id"><span v-if="cloning===f.id"><span class="spinner-border spinner-border-sm me-1"></span></span><i v-else class="mdi mdi-content-copy me-1"></i>Clone</button>
              </div>
            </div>
            <div v-if="f.description" class="ff-desc">{{ f.description }}</div>
          </div>

          <h6 class="ff-section mt-4">Your formulations</h6>
          <div v-if="ownList.length === 0" class="ff-empty">
            <i class="mdi mdi-clipboard-text-outline" style="font-size:36px;color:#6B6356;"></i>
            <p class="text-muted mt-2 mb-0">No custom formulations yet. Clone a template above or start fresh with <strong>New</strong>.</p>
          </div>
          <div v-for="f in ownList" :key="'own-'+f.id" class="ff-card">
            <div class="ff-card-head">
              <div>
                <strong>{{ f.name }}</strong>
                <div class="ff-meta">
                  <span v-if="f.weight_band_min_kg || f.weight_band_max_kg"><i class="mdi mdi-scale me-1"></i>{{ f.weight_band_min_kg || '?' }}-{{ f.weight_band_max_kg || '?' }}kg</span>
                  <span><i class="mdi mdi-target me-1"></i>{{ f.target_kg_per_day }} kg/day</span>
                  <span><i class="mdi mdi-cash me-1"></i>{{ Number(f.daily_cost_kshs).toLocaleString() }} Ksh/day</span>
                </div>
              </div>
              <div class="d-flex gap-1">
                <button class="btn btn-sm btn-outline-warning" @click="openEdit(f.id)"><i class="mdi mdi-pencil"></i></button>
                <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(f)"><i class="mdi mdi-delete"></i></button>
              </div>
            </div>
            <div v-if="f.description" class="ff-desc">{{ f.description }}</div>
          </div>
        </div>
      </div>

      <!-- EDITOR MODAL -->
      <div v-if="showEditor" class="ff-overlay" @click.self="showEditor=false">
        <div class="ff-modal">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="m-0 fw-bold" style="color:var(--fm-h,#F5F0E8);">{{ form.id ? 'Edit formulation' : (viewOnly ? 'View formulation' : 'New formulation') }}</h6>
            <button class="btn btn-link text-muted" @click="showEditor=false"><i class="mdi mdi-close"></i></button>
          </div>

          <div class="row g-2 mb-2">
            <div class="col-12"><label class="ff-label">Farm *</label>
              <select v-model="form.farm_id" :disabled="viewOnly" class="ff-input w-100" @change="onFarmChange">
                <option value="">Select farm...</option>
                <option v-for="fm in farms" :key="fm.id" :value="fm.id">{{ fm.name }}</option>
              </select>
              <div style="font-size:9px;color:var(--fm-s,#6B6356);margin-top:1px;">Ingredients are picked from this farm's feed stock, and feeds deduct from it.</div>
            </div>
            <div class="col-12"><label class="ff-label">Name *</label><input v-model="form.name" :readonly="viewOnly" class="ff-input w-100" /></div>
            <div class="col-12"><label class="ff-label">Notes</label><textarea v-model="form.description" :readonly="viewOnly" class="ff-input w-100" rows="2"></textarea></div>
            <div class="col-4"><label class="ff-label">Weight band min (kg)</label><input v-model.number="form.weight_band_min_kg" :readonly="viewOnly" type="number" step="0.1" class="ff-input w-100" /><div class="ff-hint">Lightest animal this ration suits — used to match it to animals by weight.</div></div>
            <div class="col-4"><label class="ff-label">Weight band max (kg)</label><input v-model.number="form.weight_band_max_kg" :readonly="viewOnly" type="number" step="0.1" class="ff-input w-100" /><div class="ff-hint">Heaviest animal this ration suits.</div></div>
            <div class="col-4"><label class="ff-label">Target kg/day</label><input v-model.number="form.target_kg_per_day" :readonly="viewOnly" type="number" step="0.01" class="ff-input w-100" /><div class="ff-hint">Expected daily weight gain on this feed — used to project days to target weight.</div></div>
            <div class="col-6"><label class="ff-label">Max purchase price (Ksh/kg)</label><input v-model.number="form.max_purchase_price_kshs_per_kg" :readonly="viewOnly" type="number" step="1" class="ff-input w-100" /><div class="ff-hint">Most you'd pay per kg to buy an animal for this ration and still profit — drives the Buy / Don't-Buy advice on listings.</div></div>
            <div class="col-6"><label class="ff-label">Sale target (Ksh/kg)</label><input v-model.number="form.sale_target_kshs_per_kg" :readonly="viewOnly" type="number" step="1" class="ff-input w-100" /><div class="ff-hint">Price per kg liveweight you expect at sale — used for the profit projection.</div></div>
          </div>

          <div class="ff-items-head">
            <h6 class="m-0" style="color:#BFA164;font-size:13px;">Ingredients</h6>
            <button v-if="!viewOnly" class="btn btn-sm btn-outline-warning" @click="addItem"><i class="mdi mdi-plus"></i> Add</button>
          </div>
          <div class="table-responsive">
            <table class="table table-sm ff-items">
              <thead><tr><th>Ingredient (from stock)</th><th style="width:90px;">Qty</th><th style="width:60px;">UOM</th><th style="width:100px;">Ksh / unit</th><th style="width:80px;">Total</th><th v-if="!viewOnly" style="width:30px;"></th></tr></thead>
              <tbody>
                <tr v-for="(it,ii) in form.items" :key="'fi-'+ii">
                  <td>
                    <select v-if="!viewOnly" v-model="it.stock_id" class="ff-input w-100" @change="onStockPick(it)" style="margin-bottom:3px;">
                      <option value="">— Free-form —</option>
                      <option v-for="s in farmFeedStock" :key="s.id" :value="s.id">{{ s.item_name }} ({{ Number(s.current_quantity).toLocaleString() }} {{ s.unit }})</option>
                    </select>
                    <div v-if="!viewOnly && !form.farm_id" style="font-size:9px;color:#E0A800;margin-bottom:2px;">Select a farm above to pick from stock.</div>
                    <input v-model="it.ingredient_name" :readonly="viewOnly || !!it.stock_id" class="ff-input w-100" placeholder="Ingredient name" />
                    <div v-if="it.stock_id" style="font-size:9px;color:#50C878;margin-top:1px;"><i class="mdi mdi-link-variant"></i> deducts from stock</div>
                  </td>
                  <td><input v-model.number="it.quantity_kg" :readonly="viewOnly" type="number" step="0.1" class="ff-input w-100" /></td>
                  <td><input v-model="it.uom" :readonly="viewOnly || !!it.stock_id" class="ff-input w-100" /></td>
                  <td><input v-model.number="it.unit_cost_kshs" :readonly="viewOnly || !!it.stock_id" type="number" step="0.5" class="ff-input w-100" /></td>
                  <td class="ff-total">{{ Number((it.quantity_kg||0) * (it.unit_cost_kshs||0)).toLocaleString() }}</td>
                  <td v-if="!viewOnly"><button class="btn btn-sm btn-link text-danger p-0" @click="form.items.splice(ii,1)"><i class="mdi mdi-close"></i></button></td>
                </tr>
                <tr v-if="!form.items.length"><td :colspan="viewOnly?5:6" class="text-center text-muted">No ingredients yet.</td></tr>
              </tbody>
              <tfoot><tr><td colspan="4" class="text-end fw-bold">Daily ration cost</td><td class="ff-total fw-bold">{{ Number(dailyCost).toLocaleString() }}</td><td v-if="!viewOnly"></td></tr></tfoot>
            </table>
          </div>

          <div v-if="!viewOnly" class="d-flex gap-2 mt-3">
            <button class="btn btn-link text-muted flex-fill" @click="showEditor=false">Cancel</button>
            <button class="btn btn-warning flex-fill" @click="save" :disabled="!canSave||saving">
              <span v-if="saving"><span class="spinner-border spinner-border-sm me-1"></span>Saving…</span>
              <span v-else><i class="mdi mdi-check me-1"></i>Save</span>
            </button>
          </div>
          <div v-else class="d-flex mt-3">
            <button class="btn btn-warning w-100" @click="showEditor=false">Close</button>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "FeedFormulations",
  data() {
    return {
      darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
      loading: true, saving: false, cloning: null,
      list: [], feedStock: [], farms: [],
      showEditor: false, viewOnly: false,
      form: { id: null, farm_id: '', name: '', description: '', weight_band_min_kg: null, weight_band_max_kg: null, target_kg_per_day: 1.0, max_purchase_price_kshs_per_kg: null, sale_target_kshs_per_kg: 300, items: [] }
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    templateList(){return this.list.filter(function(f){return Number(f.is_template)===1})},
    ownList(){return this.list.filter(function(f){return Number(f.is_template)!==1})},
    dailyCost(){var s=0;(this.form.items||[]).forEach(function(it){s+=Number(it.quantity_kg||0)*Number(it.unit_cost_kshs||0)});return s.toFixed(2)},
    farmFeedStock(){var self=this;if(!this.form.farm_id)return [];return (this.feedStock||[]).filter(function(s){return String(s.farm_id)===String(self.form.farm_id)})},
    canSave(){return !!this.form.farm_id && !!this.form.name && (this.form.items||[]).some(function(it){return it.ingredient_name && Number(it.quantity_kg)>0})}
  },
  mounted(){this.load()},
  methods: {
    async load(){
      this.loading = true;
      var uid = sessionStorage.getItem('userId')||'';
      try {
        var r = await executeJson({function:'getFeedFormulations', ownerId: uid}, flocker);
        if (r.data && r.data.success) this.list = r.data.data.formulations || [];
        var sr = await executeJson({function:'getOwnerFeedStock', ownerId: uid}, flocker);
        if (sr.data && sr.data.success) this.feedStock = sr.data.data.stock || [];
        var fr = await executeJson({function:'getMyFarms', ownerId: uid}, flocker);
        if (fr.data && fr.data.success) this.farms = fr.data.data.farms || [];
      } catch(e){ console.error(e); }
      this.loading = false;
    },
    openNew(){
      this.viewOnly = false;
      this.form = { id: null, farm_id: (this.farms.length===1 ? this.farms[0].id : ''), name: '', description: '', weight_band_min_kg: null, weight_band_max_kg: null, target_kg_per_day: 1.0, max_purchase_price_kshs_per_kg: null, sale_target_kshs_per_kg: 300, items: [] };
      this.showEditor = true;
    },
    onFarmChange(){
      // Switching farms invalidates any stock links from the previous farm — clear them.
      (this.form.items||[]).forEach(function(it){ it.stock_id=''; });
    },
    async openEdit(id){ await this.openForm(id, false); },
    async openView(id){ await this.openForm(id, true); },
    async openForm(id, readonly){
      this.viewOnly = readonly;
      try {
        var r = await executeJson({function:'getFeedFormulation', formulationId: id}, flocker);
        if (r.data && r.data.success){
          var f = r.data.data.formulation;
          this.form = {
            id: Number(f.id),
            farm_id: f.farm_id || '',
            name: f.name, description: f.description || '',
            weight_band_min_kg: f.weight_band_min_kg ? Number(f.weight_band_min_kg) : null,
            weight_band_max_kg: f.weight_band_max_kg ? Number(f.weight_band_max_kg) : null,
            target_kg_per_day: f.target_kg_per_day ? Number(f.target_kg_per_day) : null,
            max_purchase_price_kshs_per_kg: f.max_purchase_price_kshs_per_kg ? Number(f.max_purchase_price_kshs_per_kg) : null,
            sale_target_kshs_per_kg: f.sale_target_kshs_per_kg ? Number(f.sale_target_kshs_per_kg) : 300,
            items: (r.data.data.items||[]).map(function(it){return{ingredient_name:it.ingredient_name, stock_id:it.stock_id||'', quantity_kg:Number(it.quantity_kg), uom:it.uom||'KGS', unit_cost_kshs:Number(it.unit_cost_kshs)}})
          };
          this.showEditor = true;
        }
      } catch(e){ alert('Failed to load formulation'); }
    },
    addItem(){this.form.items.push({ingredient_name:'',stock_id:'',quantity_kg:0,uom:'KGS',unit_cost_kshs:0})},
    onStockPick(it){
      if(!it.stock_id){return}
      var self=this;var s=(this.feedStock||[]).find(function(x){return String(x.id)===String(it.stock_id)});
      if(s){it.ingredient_name=s.item_name;it.uom=s.unit||'kg';it.unit_cost_kshs=Number(s.unit_cost||0)}
    },
    async save(){
      this.saving = true;
      try {
        var payload = {
          function: 'saveFeedFormulation', ownerId: sessionStorage.getItem('userId')||'',
          farmId: this.form.farm_id || '',
          name: this.form.name, description: this.form.description,
          weightBandMinKg: this.form.weight_band_min_kg, weightBandMaxKg: this.form.weight_band_max_kg,
          targetKgPerDay: this.form.target_kg_per_day,
          maxPurchasePricePerKg: this.form.max_purchase_price_kshs_per_kg,
          saleTargetPerKg: this.form.sale_target_kshs_per_kg,
          items: JSON.stringify(this.form.items)
        };
        if (this.form.id) payload.id = this.form.id;
        var r = await executeJson(payload, flocker);
        if (r.data && r.data.success){ this.showEditor = false; await this.load(); }
        else alert(r.data.message||'Failed to save');
      } catch(e){ alert('Error saving formulation'); }
      this.saving = false;
    },
    async cloneTemplate(f){
      this.cloning = f.id;
      try {
        var r = await executeJson({function:'cloneFeedFormulation', formulationId: f.id, ownerId: sessionStorage.getItem('userId')||''}, flocker);
        if (r.data && r.data.success) await this.load();
        else alert(r.data.message||'Failed to clone');
      } catch(e){ alert('Error cloning'); }
      this.cloning = null;
    },
    async confirmDelete(f){
      if (!confirm('Delete "'+f.name+'"? This cannot be undone.')) return;
      try {
        var r = await executeJson({function:'deleteFeedFormulation', formulationId: f.id, ownerId: sessionStorage.getItem('userId')||''}, flocker);
        if (r.data && r.data.success) await this.load();
        else alert(r.data.message||'Failed to delete');
      } catch(e){ alert('Error deleting'); }
    }
  }
};
</script>

<style scoped>
.ff-topbar{background:var(--fm-bg1,#141414);border-bottom:1px solid var(--fm-br,#1E1E1E);padding:12px 0;position:sticky;top:0;z-index:10}
html[data-theme="light"] .ff-topbar{background:#FFFFFF!important;border-color:#E8E2DA!important}
.ff-back{color:#BFA164;text-decoration:none;font-size:13px;font-weight:600}
.ff-intro{background:rgba(191,161,100,.06);border:1px solid rgba(191,161,100,.18);border-radius:8px;padding:10px 12px;font-size:12px;color:var(--fm-m,#A09888)}
.ff-section{color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;margin-bottom:8px}
.ff-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:12px;margin-bottom:8px}
html[data-theme="light"] .ff-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
.ff-card-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.ff-card-head strong{color:var(--fm-h,#F5F0E8);font-size:14px}
.ff-meta{display:flex;gap:10px;flex-wrap:wrap;font-size:11px;color:var(--fm-m,#A09888);margin-top:4px}
.ff-desc{font-size:11px;color:var(--fm-s,#6B6356);margin-top:6px;line-height:1.4}
.ff-empty{text-align:center;padding:32px 16px;background:var(--fm-bg1,#141414);border:1px dashed var(--fm-br,#2A2520);border-radius:10px}
html[data-theme="light"] .ff-empty{background:#FFFFFF!important}
.ff-overlay{position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:9999;display:flex;justify-content:center;align-items:flex-start;padding:24px;overflow-y:auto}
.ff-modal{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#2A2520);border-radius:12px;padding:20px;width:100%;max-width:680px;max-height:calc(100vh - 48px);overflow-y:auto}
html[data-theme="light"] .ff-modal{background:#FFFFFF!important;border-color:#E8E2DA!important}
.ff-label{display:block;font-size:11px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.3px;margin-bottom:4px}
.ff-hint{font-size:9px;line-height:1.25;color:var(--fm-s,#6B6356);margin-top:3px;opacity:.85}
.ff-input{background:var(--fm-bg2,#0F0F0F);border:1px solid var(--fm-br,#2A2520);color:var(--fm-h,#F5F0E8);border-radius:6px;padding:8px 10px;font-size:13px;width:100%}
html[data-theme="light"] .ff-input{background:#F7F5F2!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
.ff-items-head{display:flex;justify-content:space-between;align-items:center;margin-top:12px;margin-bottom:6px}
.ff-items{font-size:12px;color:var(--fm-h,#F5F0E8)}
.ff-items th{color:#BFA164;font-weight:600;font-size:11px;text-transform:uppercase;border:none;background:transparent}
.ff-items td{border-color:var(--fm-br,#2A2520);vertical-align:middle}
.ff-total{font-weight:600;color:#FF9800}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .ff-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .ff-card { padding: 12px !important; border-radius: 10px !important; }
  .ff-input { font-size: 16px !important; }
  .ff-items { font-size: 11px; }
}
</style>
